import { HStack, Text } from "@chakra-ui/layout";
import { Trans, t } from "@lingui/macro";
import {
  type ProfitLossReportColumns,
  ProfitLossReportRows,
  ProfitLossReportWhereColumn,
  SqlOperator,
} from "@src/__generated__/graphql";
import { ProfitAndLossBalance } from "@src/components/modules/reports/profit-and-loss/components";
import type { ProfitAndLossBodyColumn } from "@src/components/modules/reports/profit-and-loss/store";
import { ACTIONS_COLUMN_ID, Link } from "@src/components/ui-kit";
import { Filter, Filters } from "@src/utils/components/filters/models";
import { WrapComponent } from "@src/utils/components/WrapComponent";
import { toApiDate } from "@src/utils/dates";
import type { DateRange } from "@src/utils/types";
import type { Route } from "nextjs-routes";

export function buildProfitAndLossColumns(
  titles: string[],
  rows: ProfitLossReportRows,
  columnsEnum: ProfitLossReportColumns | undefined,
  dateRange: DateRange,
): ProfitAndLossBodyColumn[] {
  let pathname: Route["pathname"] | null = null;

  switch (rows) {
    case ProfitLossReportRows.Project:
      pathname = "/projects/detail";
      break;
    case ProfitLossReportRows.Brand:
      pathname = "/brands/detail";
      break;
    case ProfitLossReportRows.Client:
      pathname = "/clients/detail";
      break;
    default:
      pathname = null;
      break;
  }

  const columns: ProfitAndLossBodyColumn[] = [
    {
      id: "title",
      meta: { label: t`Title` },
      header: () => t`Title`,
      cell: ({ row }) => {
        const currentRow = row.original.row;

        if (row.original.rowType === "group") {
          return (
            <WrapComponent
              if={pathname !== null}
              with={(children) =>
                pathname !== null ? (
                  <Link
                    target="_blank"
                    href={{
                      pathname,
                      query: {
                        id: currentRow.id,
                      },
                    }}
                  >
                    {children}
                  </Link>
                ) : (
                  <Text>{children}</Text>
                )
              }
            >
              {currentRow.title}
            </WrapComponent>
          );
        }

        return (
          <WrapComponent
            if={rows !== ProfitLossReportRows.Project}
            with={(children) => (
              <HStack>
                {children}
                <Link
                  target="_blank"
                  href={{
                    pathname: "/reports/profit-and-loss",
                    query: buildQueryParamsForSubrow(
                      currentRow.id,
                      rows,
                      columnsEnum,
                      dateRange,
                    ),
                  }}
                >
                  <Trans>(Details)</Trans>
                </Link>
              </HStack>
            )}
          >
            <Text>{currentRow.title}</Text>
          </WrapComponent>
        );
      },
    },
  ];

  for (const title of titles) {
    columns.push({
      id: title,
      meta: { label: title },
      header: () => title,
      cell: ({ row }) => {
        const column = row.original.row.columns.find(
          (column) => column.title === title,
        );

        return (
          <ProfitAndLossBalance
            balance_amount={column?.balance_amount ?? 0}
            expenses_amount={column?.expenses_amount ?? 0}
            invoiced_amount={column?.invoiced_amount ?? 0}
          />
        );
      },
    });
  }

  columns.push({
    id: "total",
    meta: { label: t`Total` },
    header: () => t`Total`,
    cell: ({ row }) => (
      <ProfitAndLossBalance
        balance_amount={row.original.row.total.balance_amount}
        expenses_amount={row.original.row.total.expenses_amount}
        invoiced_amount={row.original.row.total.invoiced_amount}
      />
    ),
  });

  columns.push({
    id: ACTIONS_COLUMN_ID,
  });

  return columns;
}

function buildQueryParamsForSubrow(
  id: string,
  rows: ProfitLossReportRows,
  columns: ProfitLossReportColumns | undefined,
  dateRange: DateRange,
): Route["query"] | undefined {
  const enableFilters = rows !== ProfitLossReportRows.Project;

  const filters = new Filters<ProfitLossReportWhereColumn>(
    enableFilters
      ? [
          new Filter({
            column:
              rows === ProfitLossReportRows.Client
                ? ProfitLossReportWhereColumn.BrandId
                : ProfitLossReportWhereColumn.ProjectId,
            operator: SqlOperator.In,
            value: [id],
            options: [],
          }),
        ]
      : [],
  );

  return enableFilters
    ? {
        where: filters.asURLSearchParam.where,
        from: toApiDate(dateRange.start),
        to: toApiDate(dateRange.end),
        rows:
          rows === ProfitLossReportRows.Client
            ? ProfitLossReportRows.Brand
            : ProfitLossReportRows.Project,
        columns,
      }
    : undefined;
}
